/**
 * Module-scoped configuration for the membership cancellation flow.
 *
 * Per ADR decision D13: data lookups keyed by wire values (reason codes, plan
 * tiers, term names, feature codes) with no consumer outside this module live
 * here, not in the global `utils/constants.ts`. The four analytics event names
 * stay global in `USER_EVENTS` because the analytics layer owns them app-wide.
 *
 * User-facing prose is NOT here (BRD N4): each component holds the literal
 * strings it renders. The reason labels below are the sanctioned carve-out —
 * they are indexed by wire code, so they are data rather than prose.
 */

/** The six `cancelReason` wire codes sent in the DELETE body (BRD Section 5.1). */
export const CANCEL_MEMBERSHIP_REASONS = {
  TOO_EXPENSIVE: 'TOO_EXPENSIVE',
  NO_VALUE_OR_NEED: 'NO_VALUE_OR_NEED',
  BETTER_ALTERNATIVE: 'BETTER_ALTERNATIVE',
  NO_LONGER_NEEDED: 'NO_LONGER_NEEDED',
  TECHNICAL_ISSUES: 'TECHNICAL_ISSUES',
  OTHERS: 'OTHERS',
} as const;

/**
 * Radio options in Figma order.
 *
 * `TOO_EXPENSIVE` is deliberately first so the complimentary / period-ends-today
 * variant is a single `slice(1)` (BRD FR-006 / BR-002).
 *
 * Option 4 reads "I no longer need these services" — **product-supplied copy,
 * 2026-09-03**, which overrides *both* Figma `858:14102` ("I no longer need
 * privacy protection") and Angular's locale string
 * (`my-account:cancellationDetails.accountCancelModal.thinkingModal.noLongerNeeded`,
 * "I no longer need identity theft monitoring"). It is the one row in this module
 * where neither Figma nor Angular governs the copy, so do not "restore parity"
 * with either: BRD §13 row 15 and the §12 copy catalog carry the decision, and
 * CLAUDE.md rule 2's Figma-governs-copy default stays suspended here.
 */
export const CANCEL_MEMBERSHIP_REASONS_OPTIONS = [
  {
    label: 'The cost of the service is too high',
    value: CANCEL_MEMBERSHIP_REASONS.TOO_EXPENSIVE,
  },
  {
    label: 'I didn’t find the service helpful',
    value: CANCEL_MEMBERSHIP_REASONS.NO_VALUE_OR_NEED,
  },
  {
    label: 'I found a better or more affordable alternative',
    value: CANCEL_MEMBERSHIP_REASONS.BETTER_ALTERNATIVE,
  },
  {
    label: 'I no longer need these services',
    value: CANCEL_MEMBERSHIP_REASONS.NO_LONGER_NEEDED,
  },
  {
    label: 'I’ve been experiencing technical issues',
    value: CANCEL_MEMBERSHIP_REASONS.TECHNICAL_ISSUES,
  },
  {
    label: 'Other',
    value: CANCEL_MEMBERSHIP_REASONS.OTHERS,
  },
];

/**
 * Offer-card tier blurbs, keyed by the **lowercased whole** `plan.tier` string.
 *
 * Matching is a full-string lookup, never `includes()`: `premier complete` maps to
 * the premier blurb and must not fall through to `complete` (BRD FR-037 / D9).
 * This corrects Angular's `*ngSwitchCase="'Value' || 'Value Essential'"`, which
 * collapses to `'Value'` and leaves most tiers with no description at all.
 *
 * An unrecognised tier renders no description.
 */
export const TIER_DESCRIPTIONS: Record<string, string> = {
  basic:
    'Essential identity protection for individuals seeking foundational coverage.',
  value:
    'Comprehensive identity protection with extensive monitoring for peace of mind.',
  'value essential':
    'Comprehensive identity protection with extensive monitoring for peace of mind.',
  total:
    'Advanced identity protection designed for individuals focused on credit information.',
  'total plus':
    'Advanced identity protection designed for individuals focused on credit information.',
  complete:
    'Robust identity protection with added tools for broader digital security.',
  premier:
    'Powerful protection featuring the latest innovations in online safety.',
  'premier complete':
    'Powerful protection featuring the latest innovations in online safety.',
  ultimate:
    'Our most complete coverage, combining advanced identity and privacy protection.',
};

/**
 * Offer-card price suffixes — two keys, deliberately.
 *
 * The product supports monthly and annual billing terms and nothing else, so
 * Angular's `quarterlyAbbr` (`/3mo`), `biannualAbbr` (`/6mo`) and `biennialAbbr`
 * (`/2yr`) are **not ported** (BRD FR-034). Two keys is the intended state, not an
 * oversight. Selection goes through `isMonthlyTerm(termName)` from
 * `services/enrollment/utils.ts` — do not build a term map.
 */
export const TERM_ABBREVIATIONS = {
  monthly: '/mo',
  annual: '/yr',
} as const;

/**
 * Feature codes present on the plan but hidden from the offer card.
 *
 * Filtered out **before** the 6-row cap so a suppressed code never consumes a
 * display slot. This is the D7 correction to Angular, which slices first and can
 * therefore render fewer than 6 rows (BRD FR-017 / BR-007 / N7).
 */
export const SUPPRESSED_FEATURES = [
  'xpn_bundle',
  'socket_pull',
  'verification',
];

/**
 * Feature codes Angular's `plan.getFeatures()` strips before display
 * (`enrollment/domain/plan.ts:45-48`). Stripped here for the same reason.
 */
export const STRIPPED_FEATURES = [
  'family_plan',
  'identity_theft_insurance_1mil',
];

/** Max characters accepted in the "Other" free-text field (BRD FR-009). */
export const CANCEL_DESCRIPTION_MAX_LENGTH = 500;

/** Visible feature rows per offer card; the container height is fixed regardless. */
export const OFFER_CARD_MAX_FEATURES = 6;

/** Offer cards per view on web. Mobile stacks every card (BRD BR-009 / FR-062). */
export const OFFER_CARDS_PER_VIEW = 2;

/**
 * Eligibility reason codes that render the "Cancel membership" entry link but
 * leave it non-interactive (BRD FR-002 / BR-001).
 *
 * These codes are now the *sole* input to the disabled state, matching Angular's
 * `billing-term.component.html:35` exactly. `user.status` is deliberately not
 * consulted — see `selectCancelLinkDisabled` for why the former
 * `pending cancellation` precedence (BR-024 / FR-003) was removed.
 *
 * In practice this list already covers the cancelling member: `pending
 * cancellation` almost always arrives with `USER_CANCELLING`, so the reason code
 * disables the link without the status ever being read.
 */
export const CANCEL_BLOCKED_REASON_CODES = ['USER_CANCELLING', 'NO_PAYMENT'];

/**
 * Orchestrator steps. Step 2 renders as variant 3a (pre-confirm, buttons live,
 * the DELETE originates there) or 3b (terminal receipt, action row suppressed) —
 * see BRD FR-048 / FR-049 / BR-023 and decision D15.
 */
export const CANCEL_STEPS = {
  reasons: 0,
  downsell: 1,
  confirmation: 2,
} as const;

export type CancelStep = (typeof CANCEL_STEPS)[keyof typeof CANCEL_STEPS];

/** Which confirmation variant the confirmation step renders (BRD D15). */
export const CONFIRMATION_VARIANTS = {
  /** Member has not cancelled yet: both buttons live, "Yes, cancel" fires the DELETE. */
  preConfirm: 'preConfirm',
  /** Cancellation already accepted: action row suppressed, close X only. */
  terminal: 'terminal',
} as const;

export type ConfirmationVariant =
  (typeof CONFIRMATION_VARIANTS)[keyof typeof CONFIRMATION_VARIANTS];
